import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { IamAuthContext } from "@megaron/auth-react";
import { SignalDoc, signalDocRef } from "@megaron/docs-contracts";
import { useClientManager } from "@megaron/react-clients";

import { isSignalUnread } from "./signalUnread";

type SignalsContextValue = {
  signals: SignalDoc[];
  unreadSignals: SignalDoc[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
  markRead: (docIds: SignalDoc["docId"][]) => void;
};

const SignalsContext = createContext<SignalsContextValue>({
  signals: [],
  unreadSignals: [],
  isLoading: false,
  isError: false,
  refetch: () => Promise.resolve(),
  markRead: () => undefined,
});

const signalReadKey = (signal: SignalDoc) => `${signal.docId}:${signal.createdAt.getTime()}`;

const signalsQueryKey = (userKey: string | undefined, limit: number) =>
  ["docs", "searchSignals", userKey, { limit }] as const;

const signalsRefetchIntervalMs = 30_000;
const userIdleAfterMs = 5 * 60_000;
const userActivityEvents = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"] as const;

const useIsUserActive = (idleAfterMs: number): boolean => {
  const [isActive, setIsActive] = useState(true);
  const lastActivityAt = useRef(Date.now());

  useEffect(() => {
    const markActive = () => {
      lastActivityAt.current = Date.now();
      setIsActive(true);
    };
    const checkIdle = setInterval(() => setIsActive(Date.now() - lastActivityAt.current < idleAfterMs), 30_000);

    userActivityEvents.forEach((event) => window.addEventListener(event, markActive, { passive: true }));

    return () => {
      clearInterval(checkIdle);
      userActivityEvents.forEach((event) => window.removeEventListener(event, markActive));
    };
  }, [idleAfterMs]);

  return isActive;
};

export const SignalsProvider: React.FC<{
  children: React.ReactNode;
  isAuthLoaded: boolean;
}> = ({ children, isAuthLoaded }) => {
  const auth = useContext(IamAuthContext);
  const userKey = auth.iamUser?.id;
  const signalsLimit = 1000;
  const sentReadKeys = useRef(new Set<string>());
  const isUserActive = useIsUserActive(userIdleAfterMs);
  const isEnabled = isAuthLoaded && Boolean(userKey);

  useEffect(() => {
    sentReadKeys.current.clear();
  }, [userKey]);

  const query = useClientManager("docs")
    .searchSignals()
    .useQuery(
      { limit: signalsLimit },
      {
        enabled: isEnabled,
        queryKey: [...signalsQueryKey(userKey, signalsLimit)],
        refetchInterval: isEnabled && isUserActive ? signalsRefetchIntervalMs : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
      },
    );

  const wasUserActive = useRef(isUserActive);
  const { refetch } = query;

  useEffect(() => {
    if (isEnabled && isUserActive && !wasUserActive.current) void refetch();
    wasUserActive.current = isUserActive;
  }, [isEnabled, isUserActive, refetch]);

  const setReadMutation = useClientManager("docs")
    .setSignalRead()
    .useMutation({
      onSuccess: () => {
        void query.refetch();
      },
    });

  const signals = useMemo((): SignalDoc[] => query.data?.items ?? [], [query.data?.items]);

  const unreadSignals = useMemo(() => signals.filter(isSignalUnread), [signals]);

  const signalsByDocId = useMemo(() => new Map(signals.map((signal) => [signal.docId, signal])), [signals]);

  const markRead = useCallback(
    (docIds: SignalDoc["docId"][]) => {
      const pending = docIds
        .flatMap((docId) => signalsByDocId.get(docId) ?? [])
        .filter((signal) => isSignalUnread(signal) && !sentReadKeys.current.has(signalReadKey(signal)));

      if (pending.length === 0) return;

      pending.forEach((signal) => sentReadKeys.current.add(signalReadKey(signal)));
      setReadMutation.mutate({ signals: pending.map(signalDocRef), isRead: true });
    },
    [signalsByDocId, setReadMutation],
  );

  return (
    <SignalsContext.Provider
      value={{
        signals,
        unreadSignals,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: query.refetch,
        markRead,
      }}
    >
      {children}
    </SignalsContext.Provider>
  );
};

export const useSignals = (): SignalsContextValue => useContext(SignalsContext);
